import { useEffect, useState } from 'preact/hooks';

// Tracks which section heading is currently in view so the docs sidebar can
// highlight the reader's place on the page. Observes the elements whose ids
// are passed in and reports the topmost one intersecting the viewport band
// just below the sticky header. Returns the first id until the reader scrolls.
export function useActiveSection(ids: string[]): string | null {
  const key = ids.join(',');
  const [active, setActive] = useState<string | null>(ids[0] ?? null);

  useEffect(() => {
    setActive(ids[0] ?? null);
    if (typeof IntersectionObserver === 'undefined' || ids.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort(
            (a, b) => a.boundingClientRect.top - b.boundingClientRect.top,
          );
        if (visible[0]) setActive(visible[0].target.id);
      },
      // Ignore the top 80px (behind the sticky header) and require a heading
      // to reach the upper third of the viewport before it counts as active.
      { rootMargin: '-80px 0px -66% 0px', threshold: 0 },
    );

    for (const id of ids) {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    }
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return active;
}
